import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dataFetch } from '../api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiShield, FiLock, FiVideo, FiCheckCircle } = FiIcons;

const PhishingLandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [recorded, setRecorded] = useState(false);
  const [simulation, setSimulation] = useState(null);
  
  // Parse query parameters once
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isReport = params.get('action') === 'report';

  useEffect(() => {
    const recordClick = async () => {
      if (recorded) return;
      
      const simId = params.get('simId');
      const token = params.get('token');
      
      if (simId) {
        try {
          const res = await dataFetch(`read/phishing_simulations/${simId}`);
          if (res?.status === 'success' && res.data) {
            const sim = res.data;
            setSimulation(sim);
            
            let shouldIncrementSimClick = false;
            let shouldIncrementSimReport = false;
            
            // Record recipient tracking using the secure token
            if (token) {
              const recRes = await dataFetch(`read/phishing_recipients?tracking_token=${token}`);
              if (recRes?.status === 'success' && recRes.data?.length > 0) {
                 const rec = recRes.data[0];
                 
                 // If the user clicked the explicit 'Report' link
                 if (isReport) {
                    if (rec.status !== 'Reported') {
                      shouldIncrementSimReport = true;
                      await dataFetch(`update/phishing_recipients/${rec.id}`, {
                        method: 'PUT',
                        body: JSON.stringify({ 
                          ...rec, 
                          reported: 1, 
                          reported_at: new Date().toISOString().slice(0, 19).replace('T', ' '), 
                          status: 'Reported' 
                        })
                      });
                    }
                 } else {
                    // Otherwise, they clicked the actual phishing payload link
                    if (rec.status !== 'Clicked' && rec.status !== 'Reported') {
                      shouldIncrementSimClick = true;
                      await dataFetch(`update/phishing_recipients/${rec.id}`, {
                        method: 'PUT',
                        body: JSON.stringify({ 
                          ...rec, 
                          clicked: 1, 
                          clicked_at: new Date().toISOString().slice(0, 19).replace('T', ' '), 
                          status: 'Clicked' 
                        })
                      });
                    }
                 }
              }
            }
            
            // Update simulation overall metrics count if not duplicate
            if (shouldIncrementSimClick) {
              await dataFetch(`update/phishing_simulations/${simId}`, {
                method: 'PUT',
                body: JSON.stringify({ ...sim, clicked: (sim.clicked || 0) + 1 })
              });
            } else if (shouldIncrementSimReport) {
              await dataFetch(`update/phishing_simulations/${simId}`, {
                method: 'PUT',
                body: JSON.stringify({ ...sim, reported: (sim.reported || 0) + 1 })
              });
            }
            
            setRecorded(true);
          }
        } catch (e) {
          console.error('Failed to record activity', e);
        }
      }
    };
    recordClick();
  }, [recorded, isReport, params]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-12 px-4">
      <div className="max-w-3xl w-full bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="p-8 md:p-12 text-center space-y-8">
          
          {isReport ? (
            <>
              <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto shadow-sm">
                <SafeIcon icon={FiCheckCircle} className="text-5xl text-emerald-600" />
              </div>
              
              <div className="space-y-3">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Great job! You successfully reported a phishing test.</h1>
                <p className="text-slate-600 text-lg md:text-xl">
                  Your vigilance helps keep the company secure. The email you just reported was a simulated phishing attack sent by your organization's security team.
                </p>
              </div>
            </>
          ) : (
             <>
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto shadow-sm">
                <SafeIcon icon={FiAlertTriangle} className="text-5xl text-red-600" />
              </div>
              
              <div className="space-y-3">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Oops! That was a simulated phishing test.</h1>
                <p className="text-slate-600 text-lg md:text-xl">
                  The link you just clicked was part of a simulated phishing attack sent by your organization's security team.
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 p-6 rounded-2xl text-amber-800 text-left flex flex-col md:flex-row items-start gap-4">
                <div className="bg-amber-200/50 p-3 rounded-full flex-shrink-0">
                  <SafeIcon icon={FiShield} className="text-2xl text-amber-700" />
                </div>
                <div>
                  <strong className="block text-lg mb-1">Don't worry! Your data is safe.</strong>
                  This was just a test intended for educational purposes. However, in a real attack, clicking that link could have compromised your account and the company's network.
                </div>
              </div>
            </>
          )}

          <div className="py-4">
            <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-6 flex items-center justify-center gap-3">
              <SafeIcon icon={FiVideo} className="text-blue-600" />
              Learn How to Spot Phishing
            </h3>
            <div className="w-full aspect-video bg-slate-900 rounded-2xl overflow-hidden shadow-xl border-4 border-slate-900 items-center">
              <iframe 
                className="w-full h-full" 
                src="https://www.youtube.com/embed/GZc-CpV5Z1k?si=jn5sPmFk1NAtH_dS" 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                referrerPolicy="strict-origin-when-cross-origin" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-200 flex flex-col items-center gap-5">
            <p className="text-slate-600 text-lg font-medium">Please log in to the Sentinel Platform to review your required security trainings.</p>
            <button 
              onClick={() => navigate('/login')}
              className="flex items-center justify-center gap-3 bg-slate-900 text-white px-8 py-4 rounded-xl font-bold hover:bg-slate-800 hover:-translate-y-0.5 transition-all shadow-lg hover:shadow-xl w-full sm:w-auto text-lg"
            >
              <SafeIcon icon={FiLock} className="text-xl" />
              Proceed to Sentinel Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhishingLandingPage;